<template>
  <div :class="{ '__point-boundary': true, '__is-loading': isLoading }" style="display: contents">
    <b-row v-for="(point, idx) in sequence" class="g-0" :key="point?.id">
      <div class="__point-icon __timeline">
        <i
          :class="{
            'icon-fill_home': ['START', 'END'].includes(point.type),
            'icon-outline_arrow-circle-right': point.type == 'DEFAULT',
          }"
        ></i>
      </div>

      <div class="__point-container" v-if="point">
        <div class="__point-address">
          <div class="d-flex">
            <McIconTooltip
              v-if="showIsEstimatedOutsideOperationalTime(point)"
              :id="'is-estimated-outside-operational-time-' + idx"
              :tooltip="$t('fleetTask.sequence.outsideOperationalDayTooltip')"
              :icon="'icon-fill_alert-triangle'"
              variant="warning"
              placement="bottom"
            />
            <p class="__point-title">{{ point.address?.name || '-' }}</p>
          </div>
          <p v-if="point.type == 'START'" class="__point-subtitle">
            {{ $t('fleetTask.sequence.startPoint') }}
          </p>
          <p v-else-if="point.type == 'END'" class="__point-subtitle">
            {{ $t('fleetTask.sequence.endPoint') }}
          </p>
        </div>

        <div v-if="missedRequiredFields.length == 0" class="__point-schedule">
          <template v-if="point.type === 'START'">
            <div v-if="point.estimated_departure_on" class="__arrival">
              <span class="__date">{{ toDate(point.estimated_departure_on) }}</span>
              <span class="__hour">{{ toHour(point.estimated_departure_on) }}</span>
              <span>{{ $t('fleetTask.sequence.departure') }}</span>
            </div>
          </template>

          <template v-else-if="point.type === 'END'">
            <div v-if="point.estimated_arrival_on" class="__arrival">
              <span class="__date">{{ toDate(point.estimated_arrival_on) }}</span>
              <span class="__hour">{{ toHour(point.estimated_arrival_on) }}</span>
              <span>{{ $t('fleetTask.sequence.arrive') }}</span>
            </div>
          </template>

          <template v-else>
            <div v-if="point.estimated_arrival_on" class="__arrival">
              <span class="__date">{{ toDate(point.estimated_arrival_on) }}</span>
              <span class="__hour">{{ toHour(point.estimated_arrival_on) }}</span>
              <span>{{ $t('fleetTask.sequence.arrive') }}</span>
            </div>

            <span class="__line-before"></span>
            <div
              v-if="point.estimated_arrival_on && point.estimated_departure_on"
              class="__svc-time"
            >
              <div class="__hour">
                <date-picker
                  v-if="
                    !['Perjalanan', 'Selesai'].includes(fleetTaskStatus) &&
                    !hasVendor &&
                    !isUserGroupNull
                  "
                  type="time"
                  value-type="date"
                  :show-second="false"
                  :default-value="durationToDate(point.service_time)"
                  :use12h="false"
                  :lang="datePickerLang"
                  :disabled="fleetTaskStatus === 'Menunggu Persetujuan' || isTmsMarketing"
                  confirm
                  :confirmText="$t('global.action.apply')"
                  @confirm="(date) => handleChangeServiceTime(point.id, date)"
                >
                  <template #icon-calendar>
                    <i
                      class="icon-outline_edit-2"
                      :class="{ 'is-disabled': fleetTaskStatus === 'Menunggu Persetujuan' || isTmsMarketing }"
                      role="button"
                    ></i>
                  </template>

                  <template #input>
                    <div>{{ durationToSvcTimeFormat(point.service_time) }}</div>
                  </template>
                </date-picker>

                <div v-else>{{ durationToSvcTimeFormat(point.service_time) }}</div>
              </div>

              <span class="__help">{{ $t('fleetTask.sequence.loadingUnloadingDuration') }}</span>
            </div>
            <span class="__line-after"></span>

            <div v-if="point.estimated_departure_on" class="__departure">
              <span class="__date">{{ toDate(point.estimated_departure_on) }}</span>
              <span class="__hour">{{ toHour(point.estimated_departure_on) }}</span>
              <span>{{ $t('fleetTask.sequence.departure') }}</span>
              <div
                v-if="point.buffer_estimated_departure_on && hasFeatureBufferTimeMGM"
                class="d-flex align-items-center"
              >
                {{ `(${$t('fleetTask.sequence.system')})` }}
              </div>
            </div>

            <div
              v-if="point.buffer_estimated_departure_on && hasFeatureBufferTimeMGM"
              class="__departure ms-4"
            >
              <span class="__date">{{ toDate(point.buffer_estimated_departure_on) }}</span>
              <span class="__hour">{{ toHour(point.buffer_estimated_departure_on) }}</span>
              <span>{{ $t('fleetTask.sequence.departure') }}</span>
              <div class="d-flex align-items-center gap-1">
                {{ `(${$t('fleetTask.sequence.final')})` }}
                <McInfoTooltip
                  :id="`buffer_time_${point.id || idx}`"
                  :tooltip="$t('fleetTask.sequence.bufferDepartureTooltip')"
                />
              </div>
            </div>
          </template>
        </div>

        <div v-else class="__point-alert">
          <span>
            {{ $t('fleetTask.text.noDisplayTime') }}
            <strong>{{ $t(missedRequiredFields[0].label) }}</strong>
            {{ $t('fleetTask.text.noFill') }}
          </span>
        </div>
      </div>

      <div v-if="point.type != 'END'" class="__point-distance __timeline">
        {{ toKm(sequence[idx + 1]?.distance ?? 0) }} {{ $t('fleetTask.sequence.distanceUnit') }}
      </div>

      <ActivityTable
        v-if="point.activities && point.type === 'DEFAULT'"
        :point="point"
        :missedRequiredFields="missedRequiredFields"
        :hasTmsUserGroupAccess="hasTmsUserGroupAccess"
        :isUserGroupNull="isUserGroupNull"
        :isTmsMarketing="isTmsMarketing"
        :sequence="sequence"
        :sequenceActLoad="sequenceActLoads.find(seq=>seq.id === point.id)?.activities"
      />
    </b-row>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import McIconTooltip from '@/components/McIconTooltip'
import McInfoTooltip from '@/components/McInfoTooltip'

import ActivityTable from '../ActivityTable'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import * as FleetTaskPoint from '@/services/rest-api/lib/fleet-task/point'
import datePickerLang from '@/helpers/date-picker-lang'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    ActivityTable,
    McIconTooltip,
    McInfoTooltip,
  },
  props: {
    sequence: {
      type: Array,
      default: () => [],
    },
    sequenceActLoads: {
      type: Array,
      default: () => [],
    },
    missedRequiredFields: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
    },
    hasTmsUserGroupAccess: {
      type: Boolean,
      default: false,
    },
    isUserGroupNull: {
      type: Boolean,
      default: false,
    },
    isTmsMarketing: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [Helper],
  data() {
    return {
      value: '',
    }
  },
  computed: {
    ...mapState('fleetTask', ['fleetTaskStatus', 'fleetTaskDetail']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    datePickerLang() {
      return datePickerLang()
    },
    hasVendor() {
      return this.fleetTaskDetail?.vendor?.id != null
    },
    hasFeatureBufferTimeMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.buffer_time
      )
    }
  },
  methods: {
    async handleChangeServiceTime(pointId, newValueAsDate) {
      const res = await FleetTaskPoint.update(this.$route.params.id, pointId, {
        service_time: this.dateToDuration(newValueAsDate),
      })

      if (res.errors) {
        this.$mcToast.error(this.$t('fleetTask.sequence.updateServiceTimeFailed'))
        return
      }

      // Refetch sequence
      EventBus.$emit('refetch-sequence')
    },
    durationToSvcTimeFormat(duration) {
      const dur = moment.duration(duration)

      let result = ''
      if (dur.hours() > 0) result += `${dur.hours()} ${this.$t('global.time.hours')} `
      if (dur.minutes() > 0) result += `${dur.minutes()} ${this.$t('global.time.minutes')}`

      return result
    },
    durationToDate(duration) {
      const dur = moment.duration(duration)
      return moment()
        .set({
          hours: dur.hours(),
          minutes: dur.minutes(),
          seconds: 0,
          ms: 0,
        })
        .toDate()
    },
    dateToDuration(date) {
      const startOfDay = moment().startOf('day')
      const current = moment(date).set({ seconds: 0, ms: 0 })
      const diff = current.diff(startOfDay)

      return moment.duration(diff).toISOString()
    },
    toDate(date) {
      return moment(date).format('DD MMM')
    },
    toHour(date) {
      return moment(date).format('HH:mm')
    },
    toKm(meter) {
      return parseFloat((meter / 1000).toFixed(2))
    },
    showIsEstimatedOutsideOperationalTime(point) {
      const isEstimatedOutsideOperationalTime = point.is_estimated_outside_operational_time

      return isEstimatedOutsideOperationalTime
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
